import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Table,
  Card,
  Row,
  Col,
  Tag,
  Space,
  Statistic,
  Tooltip,
  Typography,
  Button,
  Breadcrumb,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  HistoryOutlined,
  DownloadOutlined,
  HomeOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { hospitalImportApi } from '@/api/hospital-import'
import type { HospitalImport } from '@/types/hospital-import'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const { Text } = Typography

const STATUS_COLORS = {
  pending: 'default',
  processing: 'processing',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
} as const

const STATUS_ICONS = {
  pending: <ClockCircleOutlined />,
  processing: <SyncOutlined spin />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  cancelled: <CloseCircleOutlined />,
} as const

export const HospitalImportsHistory = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Calculate offset for pagination
  const offset = (page - 1) * pageSize

  // Fetch import history with React Query
  const { data, isLoading } = useQuery({
    queryKey: ['hospital-imports', page, pageSize],
    queryFn: () =>
      hospitalImportApi.getImportHistory({
        limit: pageSize,
        offset,
      }),
  })

  // Calculate statistics
  const imports = data?.data.imports || []
  const stats = {
    total: data?.data.total || 0,
    completed: imports.filter((i) => i.status === 'completed').length,
    failed: imports.filter((i) => i.status === 'failed').length,
    processing: imports.filter((i) => i.status === 'processing').length,
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return dayjs(dateString).format('MMM D, YYYY h:mm A')
  }

  const formatDuration = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return '-'
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    const seconds = end.diff(start, 'seconds')
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const columns: ColumnsType<HospitalImport> = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (filename) => (
        <Tooltip title={filename}>
          <Text>{filename}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: HospitalImport['status']) => (
        <Tag icon={STATUS_ICONS[status]} color={STATUS_COLORS[status]}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Records',
      key: 'records',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <div>
            <Text type="secondary">Total: </Text>
            <Text strong>{record.total_rows}</Text>
          </div>
          {record.status === 'completed' && (
            <>
              <div>
                <Text type="success">Imported: {record.imported_rows}</Text>
              </div>
              {record.failed_rows > 0 && (
                <div>
                  <Text type="danger">Failed: {record.failed_rows}</Text>
                </div>
              )}
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'File Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size) => <Text type="secondary">{formatFileSize(size)}</Text>,
    },
    {
      title: 'Imported By',
      dataIndex: 'imported_by',
      key: 'imported_by',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (email) => (
        <Tooltip title={email}>
          <Text>{email}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => (
        <Tooltip title={formatDate(date)}>
          <Text type="secondary">{dayjs(date).fromNow()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => (
        <Text type="secondary">{formatDuration(record.started_at, record.completed_at)}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View details">
          <Button type="link" icon={<DownloadOutlined />} disabled={record.status !== 'completed'}>
            Details
          </Button>
        </Tooltip>
      ),
    },
  ]

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <a onClick={() => navigate('/hospitals')}>
                <HomeOutlined /> Hospitals
              </a>
            ),
          },
          {
            title: 'Import History',
          },
        ]}
      />
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <HistoryOutlined style={{ fontSize: 24 }} />
            <h1 style={{ margin: 0 }}>Import History</h1>
          </Space>
        </Col>
        <Col>
          <Space size="large">
            <Statistic title="Total Imports" value={stats.total} />
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
            />
            <Statistic title="Failed" value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
            <Statistic
              title="Processing"
              value={stats.processing}
              valueStyle={{ color: '#1677ff' }}
            />
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={imports}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.data.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} imports`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage)
              setPageSize(newPageSize)
            },
          }}
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  )
}
